import { rememberedQuery } from '../lib/remember';
import { canDelete, canEdit as canEditArea } from '../lib/area';
import { DeleteButton } from '../components/DeleteButton';
import type { HistoryEntry, InvoiceItem, Lookups, Me, OrderDetail, OrderListItem } from '@ft/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { InvoiceForm } from '../components/InvoiceForm';
import { OrderForm } from '../components/OrderForm';
import { Loading, StatusPill, Use, useLoad, useToast } from '../components/ui';
import { dfmt, dtfmt, eur, eur2, pct } from '../lib/format';
import { api } from '../lib/http';
import { openFile, upload } from '../lib/download';
import { Chip, Panel } from '../lib/collapse';
import { useI18n, type T } from '../i18n';

const FIELDS = ['order_number', 'kind', 'customer_id', 'order_date', 'service_from', 'service_to', 'note', 'amount_cents', 'project_id', 'order_id', 'invoice_number', 'invoice_date', 'pdf_name', 'checked_by', 'parent_order_id'] as const;
type Field = (typeof FIELDS)[number];
const isField = (k: string): k is Field => (FIELDS as readonly string[]).includes(k);

function fmtVal(k: Field, v: unknown, lk: Lookups | null, t: T): string {
  if (v === null || v === undefined || v === '') return '–';
  if (k === 'amount_cents') return eur2(Number(v));
  if (k.endsWith('_date') || k.startsWith('service_')) return dfmt(String(v));
  if (k === 'kind') return v === 'warranty' ? t('common.warranty') : t('common.order');
  if (k === 'customer_id') return lk?.customers.find((c) => c.id === v)?.name ?? '?';
  if (k === 'project_id') return lk?.projects.find((p) => p.id === v)?.name ?? '?';
  if (k === 'order_id' || k === 'parent_order_id') return t('hist.otherOrder');
  return String(v);
}

function describe(h: HistoryEntry, lk: Lookups | null, t: T): string {
  const entries = Object.entries(h.changes).filter((e): e is [Field, unknown] => isField(e[0]));
  if (h.action === 'update') return entries.map(([k, v]) => { const [a, b] = v as [unknown, unknown]; return `${t(`field.${k}`)}: ${fmtVal(k, a, lk, t)} → ${fmtVal(k, b, lk, t)}`; }).join(' · ');
  if (h.action === 'create' && 'amount_cents' in h.changes) return eur2(Number(h.changes.amount_cents));
  return '';
}

function subject(h: HistoryEntry, t: T): string {
  const what = h.subject === 'line' ? t('hist.line', { ref: h.ref ?? '' }) : h.subject === 'invoice' ? t('hist.invoice', { ref: h.ref ?? '' }) : t('hist.order');
  return t(`hist.${h.action}`, { what });
}

export function OrderPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const { data: o, error, reload } = useLoad<OrderDetail>(`/api/orders/${id}`);
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const { data: orders, reload: reloadOrders } = useLoad<OrderListItem[]>('/api/orders');
  const [edit, setEdit] = useState(false);
  const [inv, setInv] = useState<{ invoice: InvoiceItem | null; projectId?: string } | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);
  const [toast, say] = useToast();
  const canEdit = canEditArea(me);
  const [checking, setChecking] = useState(false);

  if (!o) return <section className="page"><Loading error={error} /></section>;
  const rest = o.volumeCents - o.billedCents;
  const ready = !!lookups && !!orders;
  const grouped = o.amendments.length > 0 || !!o.parent;
  // Prüfung gegen die PDF bestätigen: PM/Vertretung eines Projekts der Bestellung oder Admin
  const canCheck = canEdit && (me.role === 'admin' || o.lines.some((l) => l.project.pm?.id === me.id || l.project.deputy?.id === me.id));

  async function confirmCheck() {
    setChecking(true);
    try { await api('POST', `/api/orders/${o!.id}/check`); reload(); say(t('order.confirmed')); } catch (e) { say((e as Error).message); } finally { setChecking(false); }
  }

  // Löschen: PM/Vertretung eines Projekts der Bestellung, Admin oder Delivery Head; Rechnungen bleiben (ohne Bestellung) erhalten
  const orderProjects = [...o.lines.map((l) => l.project), ...o.unallocated.map((u) => u.project)];
  const canDel = canDelete(me, orderProjects);
  const invProject = (i: InvoiceItem) => lookups?.projects.find((p) => p.id === i.project.id);
  async function archive() {
    setDelErr(null);
    try {
      await api('DELETE', `/api/orders/${o!.id}?version=${o!.version}${o!.invoiceCount ? '&detach=1' : ''}`);
      nav(`/bestellungen${rememberedQuery('orders')}`);
    } catch (e) {
      setDelErr((e as Error).message);
    }
  }

  return (
    <section className="page">
      <div className="crumbs"><Link to={`/bestellungen${rememberedQuery('orders')}`}>{t('nav.orders')}</Link><span>›</span><span>{o.orderNumber ?? t('common.noNumber')}</span></div>

      <div className="ohead">
        <div className="otitle">
          <div><div className="lbl">{t(`order.kind.${o.kind}`)}</div><h1>{o.orderNumber ?? t('common.noNumber')}</h1></div>
          <div style={{ paddingTop: 18 }}><StatusPill s={o.status} /></div>
          <span className="spacer" />
          <div className="ometa">
            <span>{t('order.customer')} <b>{o.customer.name}</b></span>
            <span>{t('order.orderedOn')} <b>{dfmt(o.orderDate)}</b></span>
            {(o.serviceFrom || o.serviceTo) && <span>{t('order.service')} <b>{dfmt(o.serviceFrom)} – {dfmt(o.serviceTo)}</b></span>}
            <span>{t('order.invoices')} <b>{o.invoiceCount}</b></span>
            {o.parent && <span>{t('order.amendmentOf')} <b><Link to={`/bestellungen/${o.parent.id}`}>{o.parent.orderNumber ?? t('common.noNumber')}</Link></b></span>}
          </div>
        </div>
        <div className="ofacets">
          <div className="facet"><div className="lbl">{t('orders.col.volume')}</div><div className="v num">{eur(o.volumeCents)}</div></div>
          <div className="facet"><div className="lbl">{t('orders.col.billed')}</div><div className="v num">{eur(o.billedCents)}</div>{o.volumeCents > 0 && <div className="muted small">{t('order.used', { p: pct(o.billedCents / o.volumeCents) })}</div>}</div>
          <div className="facet"><div className="lbl">{t('orders.col.rest')}</div><div className="v num" style={rest < 0 ? { color: 'var(--neg)' } : undefined}>{eur(rest)}</div></div>
          {grouped && <div className="facet"><div className="lbl">{t('order.groupTotal')}</div><div className="v num">{eur(o.groupVolumeCents)}</div><div className="muted small">{t('order.used', { p: pct(o.groupVolumeCents > 0 ? o.groupBilledCents / o.groupVolumeCents : 0) })}</div></div>}
        </div>
      </div>

      {(o.checkedBy || o.pdfName) && (
        <div className={`notice checkline-box ${o.checkedBy ? 'ok' : 'warn'}`} role="status">
          {o.checkedBy ? t('order.checkedBy', { name: o.checkedBy.name, at: o.checkedAt ? dtfmt(o.checkedAt) : '–' }) : t('order.notChecked')}
          {!o.checkedBy && canCheck && <> <button className="btn" onClick={confirmCheck} disabled={checking}>{t('order.confirmCheck')}</button></>}
        </div>
      )}

      {o.amendments.length > 0 && (
        <Panel id="od.amend" title={t('order.amendments', { n: o.amendments.length })} hint={t('order.amendmentsHint')}
          summary={<Chip>{t('order.groupTotal')} <b>{eur2(o.groupVolumeCents)}</b></Chip>}>
          <div className="tablewrap">
            <table>
              <thead><tr><th>{t('orders.col.number')}</th><th>{t('orders.col.orderedOn')}</th><th className="r">{t('orders.col.volume')}</th><th className="r">{t('orders.col.billed')}</th></tr></thead>
              <tbody>
                <tr><td>{o.orderNumber ?? t('common.noNumber')} <span className="muted small">{t('order.thisOrder')}</span></td><td className="num">{dfmt(o.orderDate)}</td><td className="r num">{eur2(o.volumeCents)}</td><td className="r num">{eur2(o.billedCents)}</td></tr>
                {o.amendments.map((a) => (
                  <tr key={a.id} className="click" tabIndex={0} onClick={() => nav(`/bestellungen/${a.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/bestellungen/${a.id}`); }}>
                    <td><span className="idlink">{a.orderNumber ?? t('common.noNumber')}</span></td><td className="num">{dfmt(a.orderDate)}</td><td className="r num">{eur2(a.volumeCents)}</td><td className="r num">{eur2(a.billedCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={2}>{t('order.groupTotal')}</td><td className="r num">{eur2(o.groupVolumeCents)}</td><td className="r num">{eur2(o.groupBilledCents)}</td></tr></tfoot>
            </table>
          </div>
        </Panel>
      )}

      {o.note && <div className="box"><div className="panel-h"><h2>{t('order.note')}</h2></div><p className="notebox">{o.note}</p></div>}

      <Panel id="od.alloc" title={t('order.alloc')} hint={t('order.allocHint')}
        summary={<><Chip>{t('order.projectsN', { n: o.lines.length })} · <b>{eur2(o.volumeCents)}</b></Chip>{o.volumeCents > 0 && <Chip tone={o.billedCents > o.volumeCents ? 'neg' : o.billedCents / o.volumeCents >= 0.9 ? 'crit' : undefined}>{t('order.usedPct', { p: Math.round((o.billedCents / o.volumeCents) * 100) })}</Chip>}</>}>
        <div className="tablewrap">
          <table>
            <thead><tr><th>{t('common.project')}</th><th>{t('common.pm')}</th><th className="r">{t('order.col.share')}</th><th className="r">{t('orders.col.billed')}</th><th className="r">{t('orders.col.rest')}</th><th>{t('orders.col.usage')}</th></tr></thead>
            <tbody>
              {o.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.project.name}</td>
                  <td>{l.project.pm?.name ?? <span className="muted">–</span>}</td>
                  <td className="r num">{eur2(l.amountCents)}</td>
                  <td className="r num">{eur2(l.billedCents)}</td>
                  <td className="r num" style={l.amountCents - l.billedCents < 0 ? { color: 'var(--neg)' } : undefined}>{eur2(l.amountCents - l.billedCents)}</td>
                  <td><Use volume={l.amountCents} billed={l.billedCents} /></td>
                </tr>
              ))}
              {o.unallocated.map((u) => (
                <tr key={u.project.id}>
                  <td>{u.project.name} <span className="pill crit">{t('order.unallocated')}</span></td>
                  <td>{u.project.pm?.name ?? <span className="muted">–</span>}</td>
                  <td className="r num">–</td>
                  <td className="r num">{eur2(u.billedCents)}</td>
                  <td className="r num">–</td>
                  <td>{canEdit && <button className="btn ghost" style={{ padding: 0 }} onClick={() => setEdit(true)} disabled={!ready}>{t('order.setShare')}</button>}</td>
                </tr>
              ))}
              {!o.lines.length && !o.unallocated.length && <tr><td colSpan={6} className="muted">{t('order.noLines')}</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel id="od.inv" title={t('order.invoicesTitle', { n: o.invoices.length })}
        summary={o.invoices.length ? <Chip><b>{eur2(o.billedCents)}</b>{o.lastInvoiceDate ? ` · ${dfmt(o.lastInvoiceDate)}` : ''}</Chip> : undefined}
        actions={canEdit ? <button className="btn" onClick={() => setInv({ invoice: null, projectId: o.lines.length === 1 ? o.lines[0]!.project.id : undefined })} disabled={!ready}>{t('order.addInvoice')}</button> : undefined}>
        <div className="tablewrap">
          {!o.invoices.length ? <div className="empty">{t('order.noInvoices')}</div> : (
            <table>
              <thead><tr><th>{t('inv.col.number')}</th><th>{t('common.date')}</th><th>{t('common.project')}</th><th className="r">{t('common.amount')}</th></tr></thead>
              <tbody>
                {o.invoices.map((i) => (
                  <tr key={i.id} className={canEdit ? 'click' : undefined} tabIndex={canEdit ? 0 : undefined} onClick={() => canEdit && ready && setInv({ invoice: i })} onKeyDown={(e) => { if (e.key === 'Enter' && canEdit && ready) setInv({ invoice: i }); }}>
                    <td className="mono">{i.invoiceNumber}</td><td className="num">{dfmt(i.invoiceDate)}</td><td>{i.project.name}</td><td className="r num">{eur2(i.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={3}>{t('common.sum')}</td><td className="r num">{eur2(o.billedCents)}</td></tr></tfoot>
            </table>
          )}
        </div>
      </Panel>

      <Panel id="od.docs" title={t('pdf.docs')} summary={<Chip>{o.pdfName ?? t('pdf.none')}</Chip>}
        actions={<>
          {o.pdfName && <button className="btn" onClick={() => openFile(`/api/orders/${o.id}/pdf`).catch((e: Error) => say(e.message))}>{t('pdf.open')}</button>}
          {canEdit && (
            <label className="btn">
              {o.pdfName ? t('pdf.replace') : t('pdf.upload')}
              <input type="file" accept="application/pdf,.pdf" hidden onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                try { await upload('PUT', `/api/orders/${o.id}/pdf`, f); reload(); say(t('pdf.saved')); } catch (err) { say((err as Error).message); }
              }} />
            </label>
          )}
        </>}>
        <p className="notebox muted">{o.pdfName ?? t('pdf.none')}</p>
      </Panel>

      <Panel id="od.hist" title={t('order.history')} hint={t('order.historyHint')}
        summary={o.history[0] ? <Chip>{t('order.historyN', { n: o.history.length })} · {dfmt(o.history[0].at.slice(0, 10))}{o.history[0].user ? ` · ${o.history[0].user}` : ''}</Chip> : undefined}>
        <ul className="log">
          {o.history.map((h, i) => (
            <li key={i}>
              <span className="ld num">{dtfmt(h.at)}</span>
              <i className={`lm${h.source === 'excel-import' ? ' imp' : ''}`} />
              <div>
                <div>{subject(h, t)}</div>
                <div className="muted small">{[describe(h, lookups, t), h.source === 'excel-import' ? t('order.fromImport') : h.source === 'workday-import' ? t('order.fromWorkday') : null, h.user].filter(Boolean).join(' · ')}</div>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {(canEdit || canDel) && (
        <div className="footbar">
          {delErr && <p className="err" style={{ marginRight: 'auto' }}>{delErr}</p>}
          {canDel && <DeleteButton what={o.orderNumber ?? ''} onDelete={archive} disabled={o.amendmentCount > 0}
            title={o.amendmentCount > 0 ? t('del.hasAmendments') : undefined}
            confirmText={o.invoiceCount ? t('del.withInvoices', { n: o.invoiceCount }) : undefined} />}
          {canEdit && <button className="btn" onClick={() => setEdit(true)} disabled={!ready}>{t('common.edit')}</button>}
          {canEdit && <button className="btn primary" onClick={() => setInv({ invoice: null, projectId: o.lines.length === 1 ? o.lines[0]!.project.id : undefined })} disabled={!ready}>{t('order.addInvoice')}</button>}
        </div>
      )}

      {edit && lookups && <OrderForm order={o} lookups={lookups} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); reload(); reloadOrders(); say(t('order.saved')); }} />}
      {inv && lookups && orders && (
        <InvoiceForm invoice={inv.invoice} preset={{ orderId: o.id, projectId: inv.projectId }} lookups={lookups} orders={orders}
          canDelete={!!inv.invoice && !!invProject(inv.invoice) && canDelete(me, [invProject(inv.invoice)!])}
          onClose={() => setInv(null)} onSaved={() => { setInv(null); reload(); reloadOrders(); say(t('invoice.saved')); }} />
      )}
      {toast}
    </section>
  );
}
